import { Component, ChangeDetectionStrategy, computed, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faSearch, faFileCertificate } from '@fortawesome/pro-duotone-svg-icons';

import { LicensePickerDialogService } from './license-picker-dialog.service';
import { LicensesService } from '../../services/licenses.service';
import { LocaleService } from '../../services/locale.service';
import { pluralKey } from '../../utils/plural.utils';
import { OperationalLabelComponent } from '../operational-label/operational-label.component';
import { LabelDescribedByDirective } from '../operational-label/label-described-by.directive';
import { L10nPluralPipe } from '../../pipes/l10n-plural.pipe';
import { daysUntil } from '../../pages/compliance/compliance.types';
import { LicenseDefinition } from '../../models/compliance.models';

/**
 * Globally-hosted, service-driven license picker — choose one library license to assign to the
 * asset(s) the caller passed. Modeled on `add-to-collection-dialog` (p-dialog + conditional search
 * past N items + a hand-rolled selectable list + click-to-pick). Reads `LicensesService.assignable`
 * (the lean, ManageLicenses ∪ AssignLicenses feed — NOT `library`). Picking fires the caller's
 * callback; the caller does the optimistic assign.
 */
@Component({
  selector: 'app-license-picker-dialog',
  standalone: true,
  imports: [FormsModule, DialogModule, InputTextModule, ProgressSpinnerModule, TranslateModule, FontAwesomeModule, OperationalLabelComponent, LabelDescribedByDirective, L10nPluralPipe],
  templateUrl: './license-picker-dialog.component.html',
  styleUrl: './license-picker-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LicensePickerDialogComponent {
  readonly faSearch = faSearch;
  readonly faFileCertificate = faFileCertificate;
  readonly daysUntil = daysUntil;

  private readonly dialogService = inject(LicensePickerDialogService);
  private readonly licensesService = inject(LicensesService);
  private readonly localeService = inject(LocaleService);

  readonly isOpen = this.dialogService.isOpen;
  readonly assetIds = this.dialogService.assetIds;
  readonly assignable = this.licensesService.assignable;

  readonly loading = signal(false);
  readonly searchQuery = signal('');

  readonly filtered = computed(() => {
    const q = this.searchQuery().toLowerCase().trim();
    const all = this.assignable();
    if (!q) return all;
    return all.filter(l => this.getName(l).toLowerCase().includes(q));
  });

  readonly title = computed(() =>
    this.assetIds().length > 1
      ? this.translate.instant(pluralKey('licenses.picker.titleMultiple', this.assetIds().length, this.localeService.activeUILanguage()), { count: this.assetIds().length })
      : this.translate.instant('licenses.picker.title'));

  private readonly translate = inject(TranslateService);

  constructor() {
    // Load the assignable feed each time the picker opens (mirrors add-to-collection's loadEffect).
    effect(async () => {
      if (!this.isOpen()) return;
      this.loading.set(true);
      try {
        await this.licensesService.loadAssignable();
      } finally {
        this.loading.set(false);
      }
    });
  }

  /** The name for the TypeScript-only chain (the search filter): an OPERATIONAL label — the interface language,
   *  then the brand's operational set (ruled 2026-09-12); the row renders it through the operational-label mount. */
  getName(def: LicenseDefinition): string {
    return this.localeService.l10nLabel(def.names, 'common.untitled');
  }

  pick(def: LicenseDefinition): void {
    this.dialogService.notifyPicked(def.id, def);
    this.close();
  }

  close(): void {
    this.dialogService.close();
    this.searchQuery.set('');
  }

  onVisibleChange(open: boolean): void {
    if (!open) this.close();
  }
}
